import { BASE_RPC_URL } from '@/constants/limitless'
import { POLYGON_RPC_URL } from '@/constants/polymarket'
import { providers } from 'ethers'
import { http, type Hex, type LocalAccount, type WalletClient, createWalletClient } from 'viem'
import { base, polygon } from 'viem/chains'
import { ViemEthersSigner } from './ethers'

/**
 * Sign a raw 32-byte digest (no EIP-191 prefix) with a viem LocalAccount.
 * Replaces the previous Account Kit `signer.inner.signRawMessage(hash)` used
 * for EIP-712 order signing. `LocalAccount.sign` is typed optional on the base
 * type but is always present on the Privy/Turnkey/private-key accounts we use —
 * same runtime guard as ViemEthersSigner.signTransaction.
 */
export function signRawHash(account: LocalAccount, hash: Hex): Promise<Hex> {
	if (typeof account.sign !== 'function') {
		throw new Error('Account does not support raw hash signing')
	}
	return account.sign({ hash })
}

export function createEvmWalletClients(account: LocalAccount): {
	walletClient: WalletClient
	baseWalletClient: WalletClient
} {
	return {
		walletClient: createWalletClient({
			account,
			chain: polygon,
			transport: http(POLYGON_RPC_URL),
		}),
		baseWalletClient: createWalletClient({
			account,
			chain: base,
			transport: http(BASE_RPC_URL),
		}),
	}
}

export async function createPolygonEthersSigner(
	walletClient: WalletClient
): Promise<ViemEthersSigner> {
	const provider = new providers.JsonRpcProvider(POLYGON_RPC_URL)
	await provider.ready
	return new ViemEthersSigner(walletClient, provider)
}
